import { useEffect, useMemo, useRef } from 'react';
import Map, { Marker } from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import { TruckTrailerIcon } from '@phosphor-icons/react';

import classNames from '@utils/classnames';
import { MAPBOX_TOKEN } from '@utils/mapbox-token.client';
import { MARKER_TINT, statusPill } from 'src/app/telemetry/status';

export interface TrailerMapMarker {
  id: number;
  /** The trailer's name or plate — what the disc names on hover and to a screen reader. */
  label: string;
  latitude: number;
  longitude: number;
  /** The provider's operational state, tinting the disc through the same table the table's pill uses. */
  status: string | null;
}

export interface TrailerMapProps {
  markers: TrailerMapMarker[];
  /** The page's single highlight — `hoveredId ?? selectedId` — so the map and the table always agree. */
  highlightedId?: number | null;
  onMarkerClick?: (id: number) => void;
  className?: string;
}

type Bounds = [[number, number], [number, number]];

/** Degrees of span below which a bounds box is a point rather than an area — roughly 50 m. */
const POINT_BOUNDS_SPAN = 0.0005;

/**
 * Ceiling for a fit that has nothing to frame. One trailer's bounds box is a single point, and Mapbox
 * answers that by zooming all the way to `maxZoom`, opening the map on one street corner.
 */
const POINT_FIT_MAX_ZOOM = 13;

/** The Netherlands, for the first render of an account whose trailers report nothing. */
const DEFAULT_CENTER: [number, number] = [5.2913, 52.1326];

/**
 * The whole fleet at once.
 *
 * A sibling of `OrderRouteMap` rather than a mode of it: that component frames one vehicle and its route,
 * this one frames however many trailers the filters left, and a `markers[]` prop on it would be two jobs
 * in one component. What it borrows are the Mapbox mechanics — the container `ResizeObserver` fix and the
 * single-point fit ceiling.
 *
 * Its table is the accessible equivalent: a marker is a pointer target, so every fact drawn here is a row
 * beside it.
 */
export const TrailerMap = ({ markers, highlightedId, onMarkerClick, className }: TrailerMapProps) => {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Fit what is drawn, not the whole fleet: dozens of trailers spread across Europe framed together open
   * on a blob where every disc overlaps. Recomputed per render, so a filter re-frames the map.
   */
  const bounds = useMemo((): Bounds | null => {
    if (markers.length === 0) {
      return null;
    }

    let minLng = markers[0].longitude;
    let maxLng = markers[0].longitude;
    let minLat = markers[0].latitude;
    let maxLat = markers[0].latitude;

    for (const marker of markers) {
      minLng = Math.min(minLng, marker.longitude);
      maxLng = Math.max(maxLng, marker.longitude);
      minLat = Math.min(minLat, marker.latitude);
      maxLat = Math.max(maxLat, marker.latitude);
    }

    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  }, [markers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !bounds) {
      return;
    }

    const [[minLng, minLat], [maxLng, maxLat]] = bounds;
    const hasExtent = maxLng - minLng >= POINT_BOUNDS_SPAN || maxLat - minLat >= POINT_BOUNDS_SPAN;

    map.fitBounds(bounds, {
      padding: 50,
      duration: 500,
      // Only a box with nothing to frame needs the ceiling.
      ...(hasExtent ? {} : { maxZoom: POINT_FIT_MAX_ZOOM }),
    });
  }, [bounds]);

  // Mapbox sizes its canvas from the container when it is made, and from then on watches the window alone —
  // so a container that reflows around it (a column growing, the table scrolling) would leave the canvas at
  // its old size, with the map stopping short of its own box. Watching the container keeps it the size of
  // the box, in both directions.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(() => mapRef.current?.getMap()?.resize());
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={className ?? 'h-80 w-full overflow-hidden rounded-lg'}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: bounds ? (bounds[0][0] + bounds[1][0]) / 2 : DEFAULT_CENTER[0],
          latitude: bounds ? (bounds[0][1] + bounds[1][1]) / 2 : DEFAULT_CENTER[1],
          zoom: 8,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
      >
        {markers.map((marker) => {
          const tint = MARKER_TINT[statusPill(marker.status)?.color ?? 'neutral'];
          const isHighlighted = highlightedId === marker.id;

          return (
            <Marker key={marker.id} longitude={marker.longitude} latitude={marker.latitude} anchor="center">
              {/* A button rather than a bare div: it is the one control the map has, so it takes focus and
                  answers the keyboard as well as the mouse. */}
              <button
                type="button"
                title={marker.label}
                aria-label={marker.label}
                onClick={() => onMarkerClick?.(marker.id)}
                className={classNames(
                  'flex cursor-pointer items-center justify-center rounded-full border-2 border-white shadow-lg transition-all',
                  tint.disc,
                  isHighlighted ? 'h-10 w-10' : 'h-8 w-8',
                )}
              >
                <TruckTrailerIcon
                  className={classNames(isHighlighted ? 'h-6 w-6' : 'h-5 w-5', tint.icon)}
                  weight="fill"
                />
              </button>
            </Marker>
          );
        })}
      </Map>
    </div>
  );
};
